import { cn } from '@/lib/cn';
import type { SemanticChangeItem } from './audit-log-semantic-diff';

interface AuditLogChangesViewProps {
  changes: SemanticChangeItem[];
  noChangesReason?: string;
  isPermissionEvent?: boolean;
}

export function AuditLogChangesView({
  changes,
  noChangesReason,
  isPermissionEvent,
}: AuditLogChangesViewProps) {
  if (changes.length === 0) {
    return (
      <div className="rounded-xl border border-gray-200 bg-gray-50/60 p-6 text-center">
        <div className="mx-auto flex h-10 w-10 items-center justify-center rounded-full bg-gray-100 text-gray-400 mb-2">
          <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <p className="text-xs font-semibold text-gray-700">
          {noChangesReason || 'No field-level changes are available for this event.'}
        </p>
        <p className="text-[11px] text-gray-500 mt-0.5">
          This operation did not modify any field states or raw snapshots were not captured.
        </p>
      </div>
    );
  }

  // If it's a permission event, render a dedicated structured permission table
  if (isPermissionEvent) {
    return (
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-2xs">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse text-xs">
            <thead>
              <tr className="border-b border-gray-200 bg-gray-50/80 text-[11px] font-bold uppercase tracking-wider text-gray-500">
                <th className="px-4 py-2.5">Section</th>
                <th className="px-4 py-2.5">Permission</th>
                <th className="px-4 py-2.5">Previous State</th>
                <th className="px-4 py-2.5">New State</th>
                <th className="px-4 py-2.5 text-right">Result</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {changes.map((item, idx) => {
                const isGranted = item.status === 'granted';
                const sectionName = item.category ? item.category.replace(/\s*Permissions$/i, '') : 'General';

                return (
                  <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                    <td className="px-4 py-2.5 font-semibold text-gray-900 whitespace-nowrap">
                      {sectionName}
                    </td>
                    <td className="px-4 py-2.5 font-medium text-gray-700 whitespace-nowrap">
                      {item.label}
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={cn(
                        'font-medium text-xs',
                        item.before === 'Allowed' ? 'text-emerald-600' : 'text-rose-600',
                      )}>
                        {item.before}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 whitespace-nowrap">
                      <span className={cn(
                        'font-semibold text-xs',
                        item.after === 'Allowed' ? 'text-emerald-600' : 'text-rose-600',
                      )}>
                        {item.after}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-right whitespace-nowrap">
                      <span className={cn(
                        'font-bold text-xs uppercase tracking-wide',
                        isGranted ? 'text-emerald-600' : 'text-rose-600',
                      )}>
                        {isGranted ? 'Granted' : 'Revoked'}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Generic / Multi-field change table
  return (
    <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-2xs">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse text-xs">
          <thead>
            <tr className="border-b border-gray-200 bg-gray-50/80 text-[11px] font-bold uppercase tracking-wider text-gray-500">
              <th className="px-4 py-2.5">Field / Attribute</th>
              <th className="px-4 py-2.5">Previous Value</th>
              <th className="px-4 py-2.5">New Value</th>
              <th className="px-4 py-2.5 text-right">Change Type</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {changes.map((item, idx) => (
              <tr key={idx} className="hover:bg-gray-50/50 transition-colors">
                <td className="px-4 py-2.5 whitespace-nowrap">
                  <div>
                    <span className="font-semibold text-gray-900 block">{item.label}</span>
                    {item.category && (
                      <span className="text-[10px] text-gray-400 block font-normal">{item.category}</span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2.5 text-xs text-rose-600 font-medium max-w-md break-words">
                  {item.before ?? 'None'}
                </td>
                <td className="px-4 py-2.5 text-xs text-emerald-600 font-semibold max-w-md break-words">
                  {item.after ?? 'None'}
                </td>
                <td className="px-4 py-2.5 text-right whitespace-nowrap">
                  <span className={cn(
                    'font-bold text-xs uppercase tracking-wide',
                    item.status === 'granted' || item.status === 'added'
                      ? 'text-emerald-600'
                      : item.status === 'revoked' || item.status === 'removed'
                        ? 'text-rose-600'
                        : item.status === 'updated'
                          ? 'text-sky-600'
                          : 'text-gray-600',
                  )}>
                    {item.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
